import type { Member } from "../../domain/app-types.ts";
import type { TaskReminder, Todo } from "../../domain/entities.ts";
import { currentTimezone, toLocalDateTime } from "../../ui/date.ts";
import { focusPanelComment, waitForTaskPanelEntry } from "../../ui/right-panel.ts";
import { recurrenceDescription } from "./recurrence.ts";
import { normalizeReminders, reminderLabel, REMINDER_OFFSETS } from "./reminders.ts";
import { composerGroupView, createTaskSubmitControlView, subtaskInputView, taskPanelToolbarView, type CreateTaskSaveMode, type TaskPanelMode } from "./task-panel-view.ts";
import { recurrenceEditorView, reminderEditorView, updateRecurrenceEditorView } from "./task-schedule-view.ts";
import type { TaskLocation } from "./task-location.ts";

export interface TaskPanelRuntimeCapabilities {
  editing(): boolean;
  selectedTaskId(): string | null;
  draftTaskId(): string | null;
  renderDetail(todo: Todo): string;
  renderComposer(todo: Todo): string;
  reminderOffsets(rootId: string): number[];
  user(): Pick<Member, "id" | "email">;
  taskMembers(collectionId: string | null): Member[];
  saveMode(): CreateTaskSaveMode;
  disclosureOpen(id: string): boolean;
  canEdit(taskId: string): boolean;
  activeRootOwner(): boolean;
  favorite(taskId: string): boolean;
  categoryName(id: string): string;
  location?(todo: Todo): TaskLocation;
  icon(name: string): string;
  motionBehavior(): ScrollBehavior;
}

export function createTaskPanelRuntime(capabilities: TaskPanelRuntimeCapabilities) {
  return {
    render: (todo: Todo): string => capabilities.editing() ? capabilities.renderComposer(todo) : capabilities.renderDetail(todo),

    reminderEditor: (todo: Todo, reminders = normalizeReminders(todo)): string => {
      const initialOffsets = !reminders.length && capabilities.draftTaskId() === todo.id && !!(todo.dueDate || todo.startAt) ? capabilities.reminderOffsets(todo.rootId) : [];
      const user = capabilities.user();
      const people = [...new Map([user, ...capabilities.taskMembers(todo.categoryId)].map(person => [person.id, person])).values()];
      return reminderEditorView({ reminders, initialOffsets, presetOffsets: REMINDER_OFFSETS, people, userId: user.id, closeIcon: capabilities.icon("close"), toLocalDateTime, reminderLabel });
    },

    recurrenceEditor: (todo: Todo): string => recurrenceEditorView(todo, currentTimezone, recurrenceDescription),
    updateRecurrenceEditor: (form: HTMLFormElement): void => updateRecurrenceEditorView(form),
    createSubmitControl: (): string => createTaskSubmitControlView(capabilities.saveMode(), capabilities.icon),
    composerGroup: (id: string, label: string, summary: string, content: string): string => composerGroupView(id, label, summary, content, capabilities.disclosureOpen(`composer:${id}`)),
    subtaskInput: (subtask: Todo["subtasks"][number]): string => subtaskInputView(subtask, capabilities.icon),
    toolbar: (todo: Todo, mode: TaskPanelMode): string => taskPanelToolbarView({ todo, mode, editable: capabilities.canEdit(todo.id), owner: capabilities.activeRootOwner(), favorite: capabilities.favorite(todo.id), categoryName: todo.categoryId ? capabilities.categoryName(todo.categoryId) : "", location: capabilities.location?.(todo), icon: capabilities.icon }),
    focusComment: (id: string): void => focusPanelComment(id, capabilities.motionBehavior()),
    afterEntry: (taskId: string): Promise<void> => waitForTaskPanelEntry(taskId, capabilities.selectedTaskId()),
  };
}
